import {
  MFG_ACCEPTER_ROLES,
  MFG_AUDITS_ROLES,
  MFG_BUILDER_ASSISTANT_ROLES,
  MFG_BUILD_PIPELINE_ROLES,
  MFG_DESK_ROLES,
  MFG_INVENTORY_COUNT_ROLES,
  MFG_PLANT_MANAGER_ROLE,
  MFG_PURCHASING_MODE_ROLES,
  MFG_PURCHASING_ROLES,
} from "@/lib/roles/turboManufacturingRoles";
import { LOGISTICS_COORDINATOR_ROLES } from "@/lib/roles/turboLogisticsRoles";
import {
  SERVICE_ADMIN_ROLES,
  SERVICE_DISPATCHER_ROLES,
  SERVICE_MATERIALS_USAGE_ROLES,
} from "@/lib/roles/turboServiceRoles";
import { SALES_REP_ROLES } from "@/lib/roles/turboSalesRoles";
import { OPS_REVIEWER_ROLES } from "@/lib/roles/turboOpsRoles";
import { LABOR_PAY_OPS_ROLES } from "@/lib/manufacturing/laborPayList";
import { AUDIENCE_DATA_ROLES } from "@/lib/ssd/ssdAudienceRoles";
import { COVERAGE_ROLES } from "@/lib/coverage/coverageRoles";

export type PrimaryMode =
  | "leads"
  | "salesIntake"
  | "dealerPlaceOrder"
  | "dealerOrderHistory"
  | "salesConsole"
  | "opportunities"
  | "formsAndFormats"
  | "mfgInventory"
  | "mfgModels"
  | "mfgPricing"
  | "mfgOverhead"
  | "mfgOrders"
  | "mfgPlants"
  | "mfgBuildPipeline"
  | "mfgLaborBilling"
  | "mfgFulfill"
  | "mfgAudits"
  | "mfgInventoryCount"
  | "mfgPurchasing"
  | "mfgWasteAssistant"
  | "mfgLaborPay"
  | "mfgBuilderAssistant"
  | "mfgQcDashboard"
  | "mfgReports"
  | "mfgAdminHome"
  | "logisticsRoutes"
  | "logisticsCalendar"
  | "logisticsCarriers"
  | "fulfillmentInventory"
  | "fulfillmentOrders"
  | "fulfillmentIntake"
  | "fulfillmentForms"
  | "fulfillmentEntry"
  | "serviceBoard"
  | "serviceRequests"
  | "serviceScheduling"
  | "serviceDefinitions"
  | "serviceMaterialsUsage"
  | "training"
  | "trainingAuthor"
  | "knowledgebase"
  | "aiPrompting"
  | "adsAssistant"
  | "adsGuidance"
  | "audienceData"
  | "coverageAssistant"
  | "locationTools"
  | "operations"
  | "dealTypes"
  | "dailyChecklist"
  | "rtoTerms"
  | "physicalInventory"
  | "opsLookup"
  | "opsObligations"
  | "dashboard"
  | "admin"
  | "dev"
  | "platform";

const SALES_INTAKE_ROLES: string[] = Array.from(
  new Set([...SALES_REP_ROLES, ...OPS_REVIEWER_ROLES, ...MFG_ACCEPTER_ROLES]),
);

const OPPORTUNITIES_ROLES: string[] = Array.from(
  new Set([...SALES_REP_ROLES, ...OPS_REVIEWER_ROLES, ...MFG_ACCEPTER_ROLES]),
);

const FULFILLMENT_ROLES = [
  "sales",
  "sales_rep",
  "admin",
  "owner",
  "dev",
  "org_admin",
  "org_owner",
  "platform_owner",
  "platform_admin",
  "platform_dev",
];

/**
 * Role lists for `canAccessPrimaryMode`. Kept in a sibling module so
 * LeadWorkspaceHeaderAccess stays under the file-size hard cap.
 */
export const TAB_ACCESS: Record<PrimaryMode, string[] | null> = {
  leads: null,
  salesIntake: SALES_INTAKE_ROLES,
  dealerPlaceOrder: [...SALES_REP_ROLES],
  dealerOrderHistory: [...SALES_REP_ROLES],
  salesConsole: null,
  opportunities: OPPORTUNITIES_ROLES,
  mfgInventory: MFG_PURCHASING_ROLES,
  mfgModels: MFG_PURCHASING_ROLES,
  mfgPricing: MFG_DESK_ROLES,
  mfgOverhead: MFG_DESK_ROLES,
  mfgOrders: MFG_DESK_ROLES,
  mfgPlants: MFG_DESK_ROLES,
  mfgBuildPipeline: MFG_BUILD_PIPELINE_ROLES,
  mfgLaborBilling: MFG_DESK_ROLES,
  mfgFulfill: [...MFG_DESK_ROLES, MFG_PLANT_MANAGER_ROLE],
  mfgAudits: MFG_AUDITS_ROLES,
  mfgInventoryCount: MFG_INVENTORY_COUNT_ROLES,
  mfgPurchasing: MFG_PURCHASING_MODE_ROLES,
  mfgWasteAssistant: MFG_PURCHASING_ROLES,
  mfgLaborPay: Array.from(new Set([...MFG_BUILD_PIPELINE_ROLES, ...LABOR_PAY_OPS_ROLES])),
  mfgBuilderAssistant: MFG_BUILDER_ASSISTANT_ROLES,
  mfgQcDashboard: MFG_BUILDER_ASSISTANT_ROLES,
  mfgReports: MFG_DESK_ROLES,
  mfgAdminHome: MFG_DESK_ROLES,
  logisticsRoutes: LOGISTICS_COORDINATOR_ROLES,
  logisticsCalendar: LOGISTICS_COORDINATOR_ROLES,
  logisticsCarriers: LOGISTICS_COORDINATOR_ROLES,
  fulfillmentInventory: FULFILLMENT_ROLES,
  fulfillmentOrders: FULFILLMENT_ROLES,
  fulfillmentIntake: FULFILLMENT_ROLES,
  fulfillmentForms: FULFILLMENT_ROLES,
  fulfillmentEntry: FULFILLMENT_ROLES,
  serviceBoard: SERVICE_DISPATCHER_ROLES,
  serviceRequests: SERVICE_ADMIN_ROLES,
  serviceScheduling: SERVICE_DISPATCHER_ROLES,
  serviceDefinitions: SERVICE_ADMIN_ROLES,
  serviceMaterialsUsage: SERVICE_MATERIALS_USAGE_ROLES,
  training: [
    "sales",
    "trainer",
    "marketing",
    "admin",
    "owner",
    "dev",
    "org_admin",
    "org_owner",
    "platform_admin",
    "platform_dev",
    "platform_trainer",
  ],
  trainingAuthor: [
    "trainer",
    "marketing",
    "admin",
    "owner",
    "dev",
    "org_admin",
    "org_owner",
    "platform_owner",
    "platform_trainer",
    "platform_admin",
    "platform_dev",
  ],
  formsAndFormats: [
    "sales",
    "sales_rep",
    "trainer",
    "marketing",
    "admin",
    "owner",
    "dev",
    "org_admin",
    "org_owner",
    "platform_owner",
    "platform_trainer",
    "platform_admin",
    "platform_dev",
  ],
  knowledgebase: [
    "trainer",
    "admin",
    "owner",
    "dev",
    "platform_admin",
    "platform_dev",
    "platform_trainer",
  ],
  aiPrompting: [
    "trainer",
    "admin",
    "owner",
    "dev",
    "org_admin",
    "org_owner",
    "platform_owner",
    "platform_admin",
    "platform_dev",
    "platform_trainer",
  ],
  adsAssistant: [
    "sales",
    "trainer",
    "marketing",
    "admin",
    "owner",
    "dev",
    "ads_guidance_editor",
    "platform_admin",
    "platform_dev",
    "platform_trainer",
  ],
  adsGuidance: [
    "admin",
    "owner",
    "org_admin",
    "org_owner",
    "marketing",
    "trainer",
    "ads_guidance_editor",
    "platform_owner",
    "platform_admin",
    "platform_dev",
    "platform_trainer",
  ],
  audienceData: [...AUDIENCE_DATA_ROLES],
  coverageAssistant: [...COVERAGE_ROLES],
  locationTools: [
    "trainer",
    "marketing",
    "admin",
    "owner",
    "dev",
    "platform_admin",
    "platform_dev",
    "platform_trainer",
  ],
  operations: OPS_REVIEWER_ROLES,
  dealTypes: OPS_REVIEWER_ROLES,
  dailyChecklist: OPS_REVIEWER_ROLES,
  physicalInventory: OPS_REVIEWER_ROLES,
  opsLookup: OPS_REVIEWER_ROLES,
  opsObligations: OPS_REVIEWER_ROLES,
  rtoTerms: [
    "admin",
    "owner",
    "org_admin",
    "org_owner",
    "dev",
    "platform_owner",
    "platform_admin",
    "platform_dev",
  ],
  dev: ["platform_owner", "platform_dev"],
  dashboard: [
    "owner",
    "admin",
    "org_owner",
    "org_admin",
    "dev",
    "platform_owner",
    "platform_admin",
  ],
  admin: [
    "owner",
    "admin",
    "org_owner",
    "org_admin",
    "dev",
    "platform_owner",
    "platform_admin",
  ],
  platform: ["platform_admin", "platform_dev", "platform_trainer"],
};
